import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import LocalidadSearch from '../LocalidadSearch';
import RutaSearch from '../RutaSearch';
import proveedorService, { type Proveedor } from '../../services/proveedorService';
import clienteService, { type PadronAfipCliente } from '../../services/clienteService';
import localidadService from '../../services/localidadService';
import estrategiaService from '../../services/estrategiaService';
import variablesSistemaService from '../../services/variablesSistemaService';
import rutaService from '../../services/rutaService';
import type { ProveedorInicialDesdePrecarga } from '../../utils/compraFacturaPrecarga';
import { normalizeCuitForStorage } from '../../utils/formatters';

const TIPO_IVA_OPCIONES: { value: string; label: string }[] = [
  { value: 'I', label: 'Responsable Inscripto' },
  { value: 'M', label: 'Monotributo' },
  { value: 'E', label: 'Exento' },
  { value: 'P', label: 'Presupuestos' },
];

const TIPOS_IVA_PROVEEDOR = new Set(['I', 'M', 'E', 'P']);

const TIPO_PROVEEDOR_OPCIONES: { value: 'C' | 'G'; label: string }[] = [
  { value: 'C', label: 'Compras' },
  { value: 'G', label: 'Gastos' },
];

const normalizarTexto = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

function armarDireccionPadronAfip(padron: PadronAfipCliente): string {
  const partes: string[] = [];
  const calle = (padron.direccion || '').trim();
  if (calle) partes.push(calle);

  const localidadPadron = (padron.localidad || '').trim();
  const cp = (padron.codigoPostal || '').trim();
  const provincia = (padron.provincia || '').trim();

  let lugar = '';
  if (localidadPadron && cp) lugar = `${localidadPadron} (${cp})`;
  else if (localidadPadron) lugar = localidadPadron;
  else if (cp) lugar = `CP ${cp}`;
  if (lugar && provincia) lugar = `${lugar}, ${provincia}`;
  else if (provincia) lugar = provincia;
  if (lugar) partes.push(lugar);

  return partes.join(', ');
}

async function buscarLocalidadDesdePadron(
  nombreLocalidad?: string | null,
  nombreProvincia?: string | null,
): Promise<LocalidadSel | null> {
  const term = (nombreLocalidad || '').trim();
  if (!term) return null;
  try {
    const response = await localidadService.getLocalidades({
      searchTerm: term,
      pageNumber: 1,
      pageSize: 50,
    });
    const items = response.items || [];
    if (items.length === 0) return null;

    const target = normalizarTexto(term);
    const exactas = items.filter((i) => normalizarTexto(i.nombreCiudad || '') === target);
    const candidatas = exactas.length ? exactas : items;
    const provincia = (nombreProvincia || '').trim();

    const mapLocalidad = (loc: (typeof items)[number]): LocalidadSel => ({
      codigoCiudad: loc.codigoCiudad,
      nombreCiudad: loc.nombreCiudad,
      nombreProvincia: loc.nombreProvincia,
    });

    if (provincia) {
      const p = normalizarTexto(provincia);
      const porProvincia = candidatas.filter((i) => {
        const np = normalizarTexto(i.nombreProvincia || '');
        return np && (np.includes(p) || p.includes(np));
      });
      if (porProvincia.length >= 1) return mapLocalidad(porProvincia[0]);
    }

    if (candidatas.length === 1) return mapLocalidad(candidatas[0]);
    if (exactas.length === 1) return mapLocalidad(exactas[0]);
    return null;
  } catch {
    return null;
  }
}

const extractApiError = (e: unknown): string => {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message ?? 'Error al guardar el proveedor';
  if (typeof data === 'string') return data;
  const d = data as Record<string, unknown>;
  if (d.message) return String(d.message);
  if (d.errors && typeof d.errors === 'object') {
    return Object.values(d.errors as Record<string, string[]>)
      .flat()
      .join(' | ');
  }
  return JSON.stringify(data).substring(0, 300);
};

interface LocalidadSel {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

interface RutaSel {
  codigoRuta: number;
  nombreRuta?: string;
}

interface Props {
  open: boolean;
  initialData: ProveedorInicialDesdePrecarga;
  onClose: () => void;
  onCreated: (proveedor: Proveedor) => void;
}

const NuevoProveedorDesdePrecargaDialog: React.FC<Props> = ({
  open,
  initialData,
  onClose,
  onCreated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loadingInit, setLoadingInit] = useState(false);
  const [nroProveedor, setNroProveedor] = useState(0);
  const [nroCuenta, setNroCuenta] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [cuit, setCuit] = useState('');
  const [tipoIva, setTipoIva] = useState('I');
  const [tipoProveedor, setTipoProveedor] = useState<'C' | 'G'>('C');
  const [direccion, setDireccion] = useState('');
  const [localidad, setLocalidad] = useState<LocalidadSel | null>(null);
  const [localidadOpen, setLocalidadOpen] = useState(false);
  const [ruta, setRuta] = useState<RutaSel | null>(null);
  const [rutaOpen, setRutaOpen] = useState(false);
  const [consultandoAfip, setConsultandoAfip] = useState(false);

  const resetForm = useCallback(() => {
    setNombre(initialData.nombre ?? '');
    setCuit(initialData.cuit ?? '');
    setTipoIva(initialData.tipoIva || 'I');
    setTipoProveedor(initialData.tipoProveedor || 'C');
    setDireccion(initialData.domicilio ?? '');
    setLocalidad(null);
    setRuta(null);
  }, [initialData]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingInit(true);
    void (async () => {
      try {
        const [proximo, estrategia, codigoRuta] = await Promise.all([
          proveedorService.getProximoNumero(),
          estrategiaService.getEstrategia(1).catch(() => null),
          variablesSistemaService.getCodigoRuta(),
        ]);
        setNroProveedor(Number(proximo) || 0);
        const cuenta = estrategia?.proveedores;
        setNroCuenta(cuenta != null && Number(cuenta) > 0 ? Number(cuenta) : null);
        try {
          const rutaApi = await rutaService.getRuta(codigoRuta);
          setRuta({
            codigoRuta: rutaApi?.codigoRuta ?? codigoRuta,
            nombreRuta: rutaApi?.nombreRuta?.trim() || undefined,
          });
        } catch {
          setRuta({ codigoRuta });
        }
      } catch {
        enqueueSnackbar('No se pudo obtener el próximo número de proveedor', { variant: 'warning' });
        setNroProveedor(0);
        setNroCuenta(null);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, [open, resetForm, enqueueSnackbar]);

  const handleConsultarAfip = async () => {
    const cuitNorm = normalizeCuitForStorage(cuit);
    if (cuitNorm.length !== 11) {
      enqueueSnackbar('Ingrese un CUIT de 11 dígitos para consultar ARCA', { variant: 'warning' });
      return;
    }

    const tieneDatos =
      Boolean(nombre.trim()) ||
      Boolean(direccion.trim()) ||
      Boolean(localidad) ||
      (Boolean(tipoIva.trim()) && tipoIva.trim().toUpperCase() !== 'I');

    if (tieneDatos) {
      const ok = window.confirm(
        'Ya hay datos cargados en el formulario. ¿Desea sobrescribirlos con la consulta a ARCA?'
      );
      if (!ok) return;
    }

    setConsultandoAfip(true);
    try {
      const padron = await clienteService.getPadronAfip(cuitNorm);
      const tipoIvaPadron = String(padron.tipoIva || '').trim().toUpperCase();
      const direccionArmada = armarDireccionPadronAfip(padron);
      const nombrePadron =
        (padron.nombre != null && String(padron.nombre).trim()) ||
        (padron.razonSocial != null && String(padron.razonSocial).trim()) ||
        '';

      setCuit(normalizeCuitForStorage(padron.cuit) || cuitNorm);
      if (nombrePadron) setNombre(nombrePadron);
      if (direccionArmada) setDireccion(direccionArmada);
      if (TIPOS_IVA_PROVEEDOR.has(tipoIvaPadron)) {
        setTipoIva(tipoIvaPadron);
      }

      const loc = await buscarLocalidadDesdePadron(padron.localidad, padron.provincia);
      if (loc) setLocalidad(loc);

      const msg =
        (padron.mensaje && String(padron.mensaje).trim()) ||
        'Datos precargados desde ARCA';
      const avisoCiudad =
        !loc && (padron.localidad || '').trim() ? ' Seleccioná la ciudad.' : '';
      enqueueSnackbar(`${msg}${avisoCiudad}`, { variant: 'success' });
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; errors?: { padronAfip?: string | string[] } } | string };
        message?: string;
      };
      const data = err?.response?.data;
      let messageText: string =
        (typeof data === 'object' && data && typeof data.message === 'string' ? data.message : '') ||
        (typeof data === 'string' ? data : '') ||
        err?.message ||
        '';

      const padronErr = typeof data === 'object' && data ? data.errors?.padronAfip : undefined;
      if (padronErr) {
        const detalle = Array.isArray(padronErr) ? padronErr.join('; ') : String(padronErr);
        messageText = messageText ? `${messageText}: ${detalle}` : detalle;
      }

      enqueueSnackbar(messageText || 'Error al consultar el padrón ARCA', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    } finally {
      setConsultandoAfip(false);
    }
  };

  const handleSubmit = async () => {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      enqueueSnackbar('Ingresá el nombre del proveedor', { variant: 'warning' });
      return;
    }
    if (!tipoIva.trim()) {
      enqueueSnackbar('Seleccioná el tipo de IVA', { variant: 'warning' });
      return;
    }
    if (!tipoProveedor) {
      enqueueSnackbar('Seleccioná el tipo de proveedor', { variant: 'warning' });
      return;
    }
    if (!direccion.trim()) {
      enqueueSnackbar('Ingresá la dirección', { variant: 'warning' });
      return;
    }
    if (!localidad?.codigoCiudad || localidad.codigoCiudad <= 0) {
      enqueueSnackbar('Seleccioná la ciudad', { variant: 'warning' });
      return;
    }
    if (!ruta?.codigoRuta || ruta.codigoRuta < 1) {
      enqueueSnackbar('Seleccioná la ruta/zona', { variant: 'warning' });
      return;
    }
    if (!nroProveedor || nroProveedor <= 0) {
      enqueueSnackbar('No hay número de proveedor disponible. Reintentá o cargá el proveedor desde el maestro.', {
        variant: 'warning',
      });
      return;
    }

    const cuitParaGuardar = normalizeCuitForStorage(cuit);
    const hoy = new Date().toISOString().split('T')[0];

    setSaving(true);
    try {
      const payload = {
        nroProveedor,
        nombre: nombreTrim,
        direccion: direccion.trim(),
        codigoCiudad: localidad.codigoCiudad,
        cuit: cuitParaGuardar === '' ? null : cuitParaGuardar,
        tipoIva: tipoIva.trim().toUpperCase(),
        tipoProveedor,
        condVta: 'CTA.CTE.',
        activo: 'A',
        codigoRuta: ruta.codigoRuta,
        nroCuenta: nroCuenta && nroCuenta > 0 ? nroCuenta : undefined,
        fechaAlta: new Date(`${hoy}T00:00:00Z`).toISOString(),
        esPersHumSucIndig: 0,
        Nombre: nombreTrim,
        Direccion: direccion.trim(),
        TipoProveedor: tipoProveedor,
      };

      const creado = await proveedorService.createProveedor(payload);
      const nro = Number(creado.nroProveedor) || nroProveedor;
      let completo: Proveedor = creado;
      try {
        completo = await proveedorService.getProveedor(nro);
      } catch {
        /* usar respuesta del alta */
      }
      enqueueSnackbar(`Proveedor #${nro} creado`, { variant: 'success' });
      onCreated(completo);
      onClose();
    } catch (e) {
      enqueueSnackbar(extractApiError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const localidadLabel = localidad
    ? [localidad.nombreCiudad, localidad.nombreProvincia].filter(Boolean).join(', ')
    : '';

  const rutaLabel = ruta
    ? `${ruta.codigoRuta}${ruta.nombreRuta?.trim() ? ` - ${ruta.nombreRuta.trim()}` : ''}`
    : '';

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Crear proveedor desde PDF</DialogTitle>
        <DialogContent dividers>
          {loadingInit ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Nº de proveedor asignado: <strong>{nroProveedor > 0 ? nroProveedor : '—'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  <TextField
                    label="CUIT"
                    fullWidth
                    size="small"
                    value={cuit}
                    onChange={(e) => setCuit(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleConsultarAfip();
                      }
                    }}
                    disabled={consultandoAfip}
                    autoFocus
                    inputProps={{ inputMode: 'numeric', maxLength: 13 }}
                    helperText="Ingresá el CUIT y consultá ARCA para precargar los datos"
                  />
                  <Button
                    variant="outlined"
                    onClick={() => void handleConsultarAfip()}
                    disabled={consultandoAfip || saving}
                    sx={{
                      whiteSpace: 'nowrap',
                      height: 40,
                      minWidth: 140,
                      textTransform: 'none',
                      flexShrink: 0,
                    }}
                  >
                    {consultandoAfip ? <CircularProgress size={20} /> : 'Consultar ARCA'}
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo IVA"
                  required
                  fullWidth
                  size="small"
                  value={tipoIva}
                  onChange={(e) => setTipoIva(e.target.value)}
                >
                  {TIPO_IVA_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo de proveedor"
                  required
                  fullWidth
                  size="small"
                  value={tipoProveedor}
                  onChange={(e) => setTipoProveedor(e.target.value as 'C' | 'G')}
                >
                  {TIPO_PROVEEDOR_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  required
                  fullWidth
                  size="small"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ciudad"
                  required
                  fullWidth
                  size="small"
                  value={localidadLabel}
                  placeholder="Buscar localidad…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setLocalidadOpen(true)}
                          aria-label="Buscar ciudad"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ruta / zona"
                  required
                  fullWidth
                  size="small"
                  value={rutaLabel}
                  placeholder="Buscar ruta…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setRutaOpen(true)}
                          aria-label="Buscar ruta"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={saving || loadingInit || consultandoAfip}
          >
            {saving ? 'Guardando…' : 'Crear y seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={localidadOpen}
        onClose={() => setLocalidadOpen(false)}
        onSelect={(loc) => {
          setLocalidad({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
          });
          setLocalidadOpen(false);
        }}
      />

      <RutaSearch
        open={rutaOpen}
        onClose={() => setRutaOpen(false)}
        onSelect={(r) => {
          if (!r.codigoRuta || r.codigoRuta < 1) return;
          setRuta({
            codigoRuta: r.codigoRuta,
            nombreRuta: r.nombreRuta,
          });
          setRutaOpen(false);
        }}
      />
    </>
  );
};

export default NuevoProveedorDesdePrecargaDialog;
